/**
 * External dependencies
 */
import type { StoreApi } from 'zustand';

/**
 * Shared dependencies
 */
import type { FirebaseStore } from '../shared/types/store';
import type { ListRecordWithItems } from '../shared/types/lists';

/**
 * Internal dependencies
 */
import {
	listsAddItems,
	listsAddSingleItem,
	listsAllWithItems,
	listsCreate,
	listsDelete,
	listsEdit,
	listsGetListItems,
	listsRemoveItems,
	recipesAll,
	recipesGetByArrayOfIds,
} from './callbacks';

import { RecipesAllParams } from './callbacks';
import {
	CreateUserListParams,
	DeleteUserListParams,
	GetRecipesParams,
	EditUserListParams,
	RemoveItemsFromUserListParams,
	AddItemsToUserListParams,
	AddSingleItemToUserListParams,
} from './requests';

/**
 * Lazily create the store only after ensuring window.valioExternals.zustand is available
 * This prevents race conditions during module initialization
 */
let storeInstance: StoreApi<FirebaseStore> | null = null;

/**
 * Reset the store instance (primarily for testing)
 * @internal
 */
export const resetStoreInstance = () => {
	storeInstance = null;
};

/**
 * Create the Firebase store instance
 * This function ensures that the store is created only once
 */
const createFirebaseStore = () => {
	if (storeInstance) {
		return storeInstance;
	}

	if (typeof window === 'undefined' || !window.valioExternals?.zustand?.createStore) {
		throw new Error(
			'window.valioExternals.zustand.createStore is not available. Ensure global.ts is loaded first.'
		);
	}

	storeInstance = window.valioExternals.zustand.createStore<FirebaseStore>((set, get) => ({
		/**
		 * This is the initial state with an empty lists record
		 * Lists will be populated by calling the `lists.all` action
		 */
		data: {
			lists: {},
		},
		/**
		 * Actions are meant to manipulate the store state
		 * We expose this to the rest of the app via `window.valioApp.store.actions`
		 */
		actions: {
			lists: {
				/**
				 * Default setter for the list
				 * This is used internally when fetching lists from the API
				 * @param {ListRecordWithItems} lists - The lists to set in the store
				 * @return {void}
				 */
				set: (lists: ListRecordWithItems): void => set({ data: { lists } }),

				all: () => listsAllWithItems({ set, get }),
				create: (params: CreateUserListParams) => listsCreate({ set, get }, params),
				get: (listId: string) => get().data.lists[listId],
				delete: (params: DeleteUserListParams) => listsDelete({ set, get }, params),
				edit: (params: EditUserListParams) => listsEdit({ set, get }, params),
				getInvitesByListId: (_listId: string) => {
					return [];
				},
				deleteInviteById: (_listId: string, _inviteId: string) => {
					// No-op
				},
				getListItems: (listId: string) => listsGetListItems({ set, get }, listId),
				addItems: (listId: string, items: AddItemsToUserListParams['items']) =>
					listsAddItems({ set, get }, listId, items),
				addSingleItem: (listId: string, item: AddSingleItemToUserListParams['item']) =>
					listsAddSingleItem({ set, get }, listId, item),
				removeItems: (listId: string, item: RemoveItemsFromUserListParams['item']) =>
					listsRemoveItems({ set, get }, listId, item),
			},
			items: {
				// TODO: Implement the create method properly with API call
				all: () => {
					const state = get();
					// Flatten all items from all lists
					return Object.values(state.data.lists).flatMap((list) => list.items);
				},
			},
			recipes: {
				all: (params: RecipesAllParams) => {
					return recipesAll({ set, get }, params);
				},
				getByArrayOfIds: (params: GetRecipesParams) => recipesGetByArrayOfIds({ set, get }, params),
			},
			invites: {
				create: (_listId: string, _email: string) => {
					// No-op
				},
				delete: (_listId: string, _inviteId: string) => {
					// No-op
				},
				getByListId: (_listId: string) => {
					return [];
				},
			},
		},
	}));

	return storeInstance;
};

/**
 * Export the store getter that lazily initializes on first access
 * This prevents the store from being created at module load time
 */
export const store = new Proxy({} as StoreApi<FirebaseStore>, {
	get(_target, prop) {
		const actualStore = createFirebaseStore();
		return actualStore[prop as keyof StoreApi<FirebaseStore>];
	},
});
